export const SUNDAY_SCHOOL_SESSION_KEY = 'sundaySchoolAdminSession';

export type SundaySchoolChurch = {
  authChurch: string;
  name: string;
  department: string;
};

export type SundaySchoolUser = {
  id: number;
  authChurch: string;
  email: string;
  name: string;
  role: string;
  staffRole?: string;
  status?: string;
  classId: number | null;
  phone?: string;
  /** 소속 부서. 관리자·전체담당자는 교회 전체 담당이라 null. */
  departmentId: number | null;
};

export type SundaySchoolSession = {
  user: SundaySchoolUser;
  church: SundaySchoolChurch;
};

export function normalizeSundaySchoolSession(value: unknown): SundaySchoolSession | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const userRaw = row.user;
  const churchRaw = row.church;
  if (!userRaw || typeof userRaw !== 'object' || !churchRaw || typeof churchRaw !== 'object') return null;

  const userRow = userRaw as Record<string, unknown>;
  const churchRow = churchRaw as Record<string, unknown>;

  const id = Number(userRow.id);
  const authChurch = String(userRow.authChurch ?? '').trim();
  const email = String(userRow.email ?? '').trim();
  if (!Number.isInteger(id) || id <= 0 || !authChurch || !email) return null;

  const churchAuthChurch = String(churchRow.authChurch ?? '').trim();
  if (!churchAuthChurch) return null;

  return {
    user: {
      id,
      authChurch,
      email,
      name: String(userRow.name ?? '').trim(),
      role: String(userRow.role ?? ''),
      staffRole: userRow.staffRole != null ? String(userRow.staffRole) : undefined,
      status: userRow.status != null ? String(userRow.status) : undefined,
      classId: userRow.classId != null ? Number(userRow.classId) : null,
      departmentId: userRow.departmentId != null ? Number(userRow.departmentId) : null,
      phone: userRow.phone != null ? String(userRow.phone) : undefined,
    },
    church: {
      authChurch: churchAuthChurch,
      name: String(churchRow.name ?? ''),
      department: String(churchRow.department ?? ''),
    },
  };
}

export function getSundaySchoolSession(): SundaySchoolSession | null {
  try {
    const raw = sessionStorage.getItem(SUNDAY_SCHOOL_SESSION_KEY);
    if (!raw) return null;
    return normalizeSundaySchoolSession(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveSundaySchoolSession(session: unknown): void {
  const normalized = normalizeSundaySchoolSession(session);
  if (!normalized) return;
  sessionStorage.setItem(SUNDAY_SCHOOL_SESSION_KEY, JSON.stringify(normalized));
}

export function clearSundaySchoolSession(): void {
  sessionStorage.removeItem(SUNDAY_SCHOOL_SESSION_KEY);
  sessionStorage.removeItem(SUNDAY_SCHOOL_DEPARTMENT_KEY);
}

// ---- 지금 보고 있는 부서 --------------------------------------------------------------
// 콘솔의 모든 조회·저장은 이 부서 기준이다(서버가 departmentId로 범위를 좁힘). 상단바에서 바꾼다.
export const SUNDAY_SCHOOL_DEPARTMENT_KEY = 'sundaySchoolAdminDepartment';

export type SundaySchoolDepartment = { id: number; name: string };

/** 교회 전체 담당(관리자·전체담당자)이 아닌 교사는 자기 부서에 고정된다. */
export function isDepartmentLocked(session: SundaySchoolSession | null): boolean {
  return !!session && !isChurchWideRole(staffRoleOf(session)) && session.user.departmentId != null;
}

export function getSelectedDepartmentId(): number | null {
  const session = getSundaySchoolSession();
  if (session && isDepartmentLocked(session)) return session.user.departmentId;
  try {
    const raw = sessionStorage.getItem(SUNDAY_SCHOOL_DEPARTMENT_KEY);
    const id = raw ? Number(JSON.parse(raw)?.id) : NaN;
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export function getSelectedDepartment(): SundaySchoolDepartment | null {
  try {
    const raw = sessionStorage.getItem(SUNDAY_SCHOOL_DEPARTMENT_KEY);
    if (!raw) return null;
    const row = JSON.parse(raw);
    const id = Number(row?.id);
    return Number.isInteger(id) && id > 0 ? { id, name: String(row.name ?? '') } : null;
  } catch {
    return null;
  }
}

export function saveSelectedDepartment(department: SundaySchoolDepartment): void {
  sessionStorage.setItem(SUNDAY_SCHOOL_DEPARTMENT_KEY, JSON.stringify(department));
}

// 웹 주일학교관리는 부서담당자·스텝 이상(관리자·전체담당자·부서담당자·스텝)만 쓴다.
// 교사(소그룹 담당)와 학부모는 주일학교 앱을 쓴다 - 교사의 학부모 가입 승인도 앱에서.
export const CONSOLE_DENIED_MESSAGE = '주일학교관리는 부서담당자·스텝 이상만 이용할 수 있어요. 교사·학부모는 주일학교 앱을 이용해 주세요.';

export function canUseSundaySchoolConsole(session: SundaySchoolSession | null): boolean {
  return !!session && session.user.role === 'teacher' && staffRoleOf(session) !== '교사';
}

export function isSundaySchoolLoggedIn(): boolean {
  return canUseSundaySchoolConsole(getSundaySchoolSession());
}

// ---- 권한 등급 ---------------------------------------------------------------------------
// 관리자(교회 최종 담당자)·전체담당자: 교회 전체 · 부서담당자: 소속 부서 전체 등록·수정
// · 교사: 맡은 소그룹(반)만 등록·수정 · 스텝: 보기만.
// 가입 승인은 바로 위 단계만(관리자→전체담당자→부서담당자→교사·스텝, 교사→학부모).
// 실제 차단은 서버가 하고, 화면은 이 값으로 버튼을 보이고 숨긴다.
export type SundaySchoolStaffRole = '관리자' | '전체담당자' | '부서담당자' | '교사' | '스텝';

export const STAFF_ROLES: SundaySchoolStaffRole[] = ['관리자', '전체담당자', '부서담당자', '교사', '스텝'];

// 서버가 시작할 때 예전 이름을 옮기지만, 그 전에 저장된 세션을 위해 화면에서도 같은 규칙으로 읽는다.
const LEGACY_STAFF_ROLE: Record<string, SundaySchoolStaffRole> = {
  전체담당: '부서담당자',
  부장: '부서담당자',
  반교사: '교사',
  담임교사: '교사',
  재정담당: '스텝',
};

export function staffRoleOf(session: SundaySchoolSession | null): SundaySchoolStaffRole {
  const raw = session?.user.staffRole ?? '';
  if ((STAFF_ROLES as string[]).includes(raw)) return raw as SundaySchoolStaffRole;
  return LEGACY_STAFF_ROLE[raw] ?? '교사';
}

/** 교회 전체(모든 부서)를 맡는 등급인지 - 관리자·전체담당자. */
export function isChurchWideRole(role: string | null | undefined): boolean {
  return role === '관리자' || role === '전체담당자';
}

/** 부서 전체를 등록·수정할 수 있는지(관리자·전체담당자·부서담당자). */
export function canManageDepartment(session: SundaySchoolSession | null): boolean {
  const role = staffRoleOf(session);
  return isChurchWideRole(role) || role === '부서담당자';
}

/** 이 반의 학생·출석을 등록·수정할 수 있는지. */
export function canEditClass(session: SundaySchoolSession | null, classId: number | null | undefined): boolean {
  if (canManageDepartment(session)) return true;
  return staffRoleOf(session) === '교사' && !!classId && session?.user.classId === Number(classId);
}

/** 스텝(보기만)인지. */
export function isReadOnlyStaff(session: SundaySchoolSession | null): boolean {
  return staffRoleOf(session) === '스텝';
}

/** 서버의 최신 역할·담당 반을 세션에 반영한다. 바뀌었으면 true. */
export async function refreshSundaySchoolPermissions(
  fetchMe: (params: { authChurch: string; userId: number }) => Promise<{
    staffRole: string;
    departmentId: number | null;
    classId: number | null;
  } | null>,
): Promise<boolean> {
  const session = getSundaySchoolSession();
  if (!session) return false;
  const me = await fetchMe({
    authChurch: session.user.authChurch,
    userId: session.user.id,
  });
  if (!me) return false;
  const changed =
    me.staffRole !== session.user.staffRole ||
    (me.classId ?? null) !== session.user.classId ||
    (me.departmentId ?? null) !== session.user.departmentId;
  if (changed) {
    saveSundaySchoolSession({
      ...session,
      user: {
        ...session.user,
        staffRole: me.staffRole,
        classId: me.classId ?? null,
        departmentId: me.departmentId ?? null,
      },
    });
  }
  return changed;
}
